import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, Check, AlertTriangle, Clock, Send, Square } from 'lucide-react';
import './WhatsAppBulk.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  const headers = { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  return fetch(url, { ...options, headers, credentials: 'include' });
}

const fmtTime = (t) => new Date(t).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });

const STATUS = {
  wachten: { label: 'In de rij', icon: Clock, cls: 'wait' },
  bezig: { label: 'Nu aan het versturen', icon: Loader2, cls: 'busy' },
  verstuurd: { label: 'Verstuurd', icon: Check, cls: 'ok' },
  mislukt: { label: 'Mislukt', icon: AlertTriangle, cls: 'fail' },
  overgeslagen: { label: 'Overgeslagen', icon: AlertTriangle, cls: 'skip' },
  gestopt: { label: 'Niet verstuurd (gestopt)', icon: Square, cls: 'skip' },
};

// Planning naar meerdere mensen tegelijk. De server verstuurt ze één voor één
// met 20-40 s ertussen; dit venster toont wie al geweest is en wie nog komt.
export default function WhatsAppBulk({ open, onClose, workers, waWeek, job, setJob, weekYear, weekNumber, dateRangeStr, makeImage }) {
  const [check, setCheck] = useState(null); // { people: {id: ok|nieuw|geen-nummer}, max }
  const [checkError, setCheckError] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [prep, setPrep] = useState(null); // { done, total }
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [round, setRound] = useState(0);

  const running = job && !job.done;
  const showJob = job && (running || job.week === weekNumber);

  // Bij openen (zonder lopende verzending) nagaan wie al een chat heeft.
  useEffect(() => {
    if (!open || running) return;
    setCheck(null); setCheckError(''); setError('');
    authFetch('/api/whatsapp/bulk-check', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jaar: weekYear, week: weekNumber, people: workers.map((w) => ({ worker_id: w.id, phone: w.phone })) }),
    }).then(async (r) => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Controleren mislukt');
      setCheck(d);
      // Standaard aangevinkt: wie het kan ontvangen en het deze week nog niet kreeg.
      setSelected(new Set(workers.filter((w) => d.people[w.id] === 'ok' && !waWeek[w.id]?.laatstVerstuurd).map((w) => w.id)));
    }).catch((e) => setCheckError(e.message));
  }, [open, weekYear, weekNumber, round, running]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aftellen tot het volgende bericht.
  useEffect(() => {
    if (!open || !running) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open, running]);

  const sendable = useMemo(() => workers.filter((w) => check?.people?.[w.id] === 'ok'), [workers, check]);
  const max = check?.max || 40;

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else if (n.size < max) n.add(id);
    return n;
  });

  const start = async () => {
    const list = workers.filter((w) => selected.has(w.id));
    if (!list.length) return;
    setError('');
    setPrep({ done: 0, total: list.length });
    try {
      const fd = new FormData();
      fd.append('jaar', weekYear);
      fd.append('week', weekNumber);
      const items = [];
      for (let i = 0; i < list.length; i++) {
        const w = list[i];
        const blob = await makeImage(w);
        fd.append(`image_${w.id}`, new File([blob], `rooster-${w.id}.png`, { type: 'image/png' }));
        const voornaam = String(w.firstName || w.name || '').trim().split(/\s+/)[0] || '';
        items.push({ worker_id: w.id, name: w.name, phone: w.phone,
          text: `Hallo${voornaam ? ` ${voornaam}` : ''},\n\nHier is je rooster voor week ${weekNumber} (${dateRangeStr}).\n\nWil je het rooster bevestigen? Reageer met een ✅, 👍 of "akkoord".` });
        setPrep({ done: i + 1, total: list.length });
      }
      fd.append('items', JSON.stringify(items));
      const res = await authFetch('/api/whatsapp/bulk', { method: 'POST', body: fd });
      const d = await res.json().catch(() => ({}));
      if (!res.ok && !d.job) throw new Error(d.error || 'Starten mislukt');
      setJob(d.job);
    } catch (e) {
      setError(e.message);
    }
    setPrep(null);
  };

  const stop = async () => {
    if (!confirm('Stoppen? Wie al verstuurd is, blijft verstuurd. De rest krijgt niets.')) return;
    const r = await authFetch('/api/whatsapp/bulk/stop', { method: 'POST' });
    const d = await r.json().catch(() => ({}));
    if (d.job) setJob(d.job);
  };

  if (!open) return null;

  const items = showJob ? job.items : [];
  const total = items.length;
  const finished = items.filter((x) => !['wachten', 'bezig'].includes(x.status)).length;
  const sent = items.filter((x) => x.status === 'verstuurd').length;
  const failed = items.filter((x) => x.status === 'mislukt').length;
  const skipped = items.filter((x) => ['overgeslagen', 'gestopt'].includes(x.status)).length;
  const next = items.find((x) => x.status === 'wachten');
  const busy = items.find((x) => x.status === 'bezig');
  const secs = job?.nextAt ? Math.max(0, Math.round((job.nextAt - now) / 1000)) : null;
  const waiting = items.filter((x) => x.status === 'wachten').length;
  const etaMin = Math.ceil((waiting * 30) / 60);

  return (
    <div className="wab-backdrop" onMouseDown={() => !prep && onClose()}>
      <div className="wab-modal" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="Planning via WhatsApp versturen">
        <div className="wab-head">
          <h3>Planning week {showJob ? job.week : weekNumber} via WhatsApp</h3>
          <button className="wab-x" onClick={onClose} disabled={!!prep} aria-label="Sluiten"><X size={18} /></button>
        </div>

        {showJob ? (
          <>
            <div className="wab-progress-top">
              <span>{job.done ? 'Klaar' : 'Bezig met versturen'} · {finished} van {total}</span>
              <span className="wab-progress-pct">{total ? Math.round((finished / total) * 100) : 0}%</span>
            </div>
            <div className="wab-bar" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={finished}>
              <div className="wab-bar-ok" style={{ width: `${total ? (sent / total) * 100 : 0}%` }} />
              <div className="wab-bar-fail" style={{ width: `${total ? (failed / total) * 100 : 0}%` }} />
              <div className="wab-bar-skip" style={{ width: `${total ? (skipped / total) * 100 : 0}%` }} />
            </div>
            <div className="wab-now">
              {busy && <><Loader2 size={15} className="wab-spin" /> Nu: <strong>{busy.name}</strong></>}
              {!busy && next && !job.stopped && <><Clock size={15} /> Volgende: <strong>{next.name}</strong>{secs != null && ` over ${secs} s`}</>}
              {!busy && job.stopped && !job.done && <>Stoppen…</>}
              {job.done && <><Check size={15} /> {sent} verstuurd{failed ? `, ${failed} mislukt` : ''}{skipped ? `, ${skipped} niet verstuurd` : ''}</>}
            </div>
            {running && waiting > 0 && <div className="wab-muted">Nog {waiting} in de rij, ongeveer {etaMin} min. Je kunt dit venster sluiten; het versturen gaat door.</div>}
            {job.error && <div className="wab-error">{job.error}</div>}

            <ul className="wab-list">
              {items.map((it) => {
                const S = STATUS[it.status] || STATUS.wachten;
                const Icon = S.icon;
                return (
                  <li key={it.worker_id} className={`wab-item ${S.cls}`}>
                    <Icon size={15} className={it.status === 'bezig' ? 'wab-spin' : ''} />
                    <span className="wab-name">{it.name}</span>
                    <span className="wab-state">
                      {it.status === 'verstuurd' && it.at ? `Verstuurd ${fmtTime(it.at)}` : S.label}
                      {it.fout ? `: ${it.fout}` : ''}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="wab-actions">
              {running
                ? <button className="wab-btn-stop" onClick={stop} disabled={job.stopped}><Square size={14} /> Stoppen</button>
                : <button className="wab-btn" onClick={() => { setJob(null); setRound((x) => x + 1); }}>Nieuwe selectie</button>}
              <button className="wab-btn-ghost" onClick={onClose}>Sluiten</button>
            </div>
          </>
        ) : (
          <>
            {!check && !checkError && <div className="wab-now"><Loader2 size={15} className="wab-spin" /> Nagaan wie al een chat heeft…</div>}
            {checkError && <div className="wab-error">{checkError}</div>}
            {check && (
              <>
                <div className="wab-select-bar">
                  <span>{selected.size} geselecteerd{selected.size ? ` · duurt ongeveer ${Math.max(1, Math.ceil((selected.size * 30) / 60))} min` : ''}</span>
                  <span className="wab-select-links">
                    <button onClick={() => setSelected(new Set(sendable.slice(0, max).map((w) => w.id)))}>Alles</button>
                    <button onClick={() => setSelected(new Set(sendable.filter((w) => !waWeek[w.id]?.laatstVerstuurd).slice(0, max).map((w) => w.id)))}>Nog niet verstuurd</button>
                    <button onClick={() => setSelected(new Set())}>Geen</button>
                  </span>
                </div>
                <ul className="wab-list">
                  {workers.map((w) => {
                    const c = check.people[w.id];
                    const ok = c === 'ok';
                    const sentAt = waWeek[w.id]?.laatstVerstuurd;
                    return (
                      <li key={w.id} className={`wab-item ${ok ? '' : 'disabled'}`}>
                        <label className="wab-check">
                          <input type="checkbox" disabled={!ok} checked={selected.has(w.id)} onChange={() => toggle(w.id)} />
                          <span className="wab-name">{w.name}</span>
                        </label>
                        <span className={`wab-state ${sentAt ? 'ok' : ''}`}>
                          {c === 'geen-nummer' && 'Geen telefoonnummer'}
                          {c === 'nieuw' && 'Nog geen chat: eerst los versturen'}
                          {ok && (sentAt ? `Al verstuurd ${new Date(sentAt).toLocaleDateString('nl-NL', { weekday: 'short' })} ${fmtTime(sentAt)}` : 'Nog niet verstuurd')}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {selected.size >= max && <div className="wab-muted">Maximaal {max} mensen per keer.</div>}
              </>
            )}
            {prep && (
              <>
                <div className="wab-progress-top"><span>Roosters klaarmaken · {prep.done} van {prep.total}</span></div>
                <div className="wab-bar"><div className="wab-bar-ok" style={{ width: `${(prep.done / prep.total) * 100}%` }} /></div>
              </>
            )}
            {error && <div className="wab-error">{error}</div>}
            <div className="wab-actions">
              <button className="wab-btn" onClick={start} disabled={!check || !selected.size || !!prep}>
                {prep ? <Loader2 size={14} className="wab-spin" /> : <Send size={14} />} Versturen naar {selected.size}
              </button>
              <button className="wab-btn-ghost" onClick={onClose} disabled={!!prep}>Annuleren</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
